<template>
  <div
    class="progress-line-chart"
    :class="{ 'is-ready': animReady, 'is-expanded': expanded, 'is-tinted': !!tint }"
    :style="tint ? { '--chart-tint': tint } : null"
  >
    <h4 v-if="title" class="progress-line-title">{{ title }}</h4>

    <div class="progress-line-body">
      <span class="progress-line-y-label">Time</span>

      <div class="progress-line-plot-wrap">
        <svg
          class="progress-line-svg"
          :viewBox="`0 0 ${vbW} ${vbH}`"
          role="img"
          :aria-label="title || 'Progress by week'"
        >
          <rect
            :x="plot.left"
            :y="plot.top"
            :width="plot.width"
            :height="plot.height"
            class="zone-bg"
          />

          <g v-for="(week, i) in weeks" :key="'w-' + week">
            <line
              :x1="plot.left"
              :y1="weekY(i)"
              :x2="plot.right"
              :y2="weekY(i)"
              class="grid-h"
            />
            <text
              :x="plot.left - 8"
              :y="weekY(i) + 3.5"
              class="axis-label axis-label--y"
              text-anchor="end"
            >Week {{ week }}</text>
          </g>

          <g v-for="tick in progressTicks" :key="'vg-' + tick">
            <line
              v-if="tick !== 0 && tick !== 100"
              :x1="progressX(tick)"
              :y1="plot.top"
              :x2="progressX(tick)"
              :y2="plot.bottom"
              class="grid-v"
            />
          </g>

          <line
            :x1="plot.left"
            :y1="plot.top"
            :x2="plot.left"
            :y2="plot.bottom"
            class="axis-line"
          />
          <line
            :x1="plot.left"
            :y1="plot.bottom"
            :x2="plot.right"
            :y2="plot.bottom"
            class="axis-line"
          />

          <g v-for="tick in progressTicks" :key="'t-' + tick">
            <line
              :x1="progressX(tick)"
              :y1="plot.bottom"
              :x2="progressX(tick)"
              :y2="plot.bottom + 4"
              class="axis-tick"
            />
            <text
              :x="progressX(tick)"
              :y="plot.bottom + 14"
              class="axis-label axis-label--x"
              text-anchor="middle"
            >{{ tick }}</text>
          </g>

          <!-- Lines -->
          <path
            v-for="line in animatedSeries"
            :key="'line-' + line.key"
            :d="line.path"
            fill="none"
            :stroke="line.color"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            :opacity="line.opacity"
            class="series-line"
          />

          <!-- Dots -->
          <circle
            v-for="(pt, idx) in animatedDots"
            :key="'dot-' + pt.key + '-' + idx"
            :cx="pt.x"
            :cy="pt.y"
            :r="pt.r"
            :fill="pt.color"
            :stroke="pt.stroke"
            :stroke-width="pt.strokeWidth"
            :opacity="pt.opacity"
          />

          <text
            :x="(plot.left + plot.right) / 2"
            :y="vbH - 4"
            class="axis-title"
            text-anchor="middle"
          >Progress %</text>
        </svg>
      </div>
    </div>

    <div v-if="showLegend && seriesMeta.length > 1" class="progress-line-legend">
      <span
        v-for="s in seriesMeta"
        :key="'leg-' + s.key"
        class="progress-line-legend-item"
      >
        <span class="legend-dot" :style="{ background: s.color }" />
        {{ s.label }}
      </span>
    </div>
  </div>
</template>

<script>
function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function pointValue(pt) {
  const value = Number(
    pt.progress != null ? pt.progress : (pt.value != null ? pt.value : pt.score)
  );
  if (!Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
}

export default {
  name: 'ProgressLineChart',
  props: {
    title: { type: String, default: '' },
    weeks: {
      type: Array,
      default() {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      },
    },
    /** [{ key, label, color, stroke?, points: [{ week, progress }] }] */
    series: {
      type: Array,
      default() {
        return [];
      },
    },
    /** Soft plot background tint, e.g. rgba(26,144,255,0.08) */
    tint: { type: String, default: '' },
    showLegend: { type: Boolean, default: true },
    expanded: { type: Boolean, default: false },
  },
  data() {
    return {
      vbW: 720,
      vbH: 280,
      plot: {
        left: 58,
        top: 14,
        right: 702,
        bottom: 238,
        width: 644,
        height: 224,
      },
      animProgress: 0,
      animReady: false,
      animFrame: null,
      animToken: 0,
    };
  },
  computed: {
    progressTicks() {
      const ticks = [];
      for (let i = 0; i <= 100; i += 10) ticks.push(i);
      return ticks;
    },
    weekIndexMap() {
      const map = {};
      this.weeks.forEach((w, i) => {
        map[Number(w)] = i;
      });
      return map;
    },
    seriesMeta() {
      return (this.series || []).map((s) => ({
        key: s.key,
        label: s.label || s.key,
        color: s.color || '#1a90ff',
        stroke: s.stroke || s.color || '#1a90ff',
      }));
    },
    normalizedSeries() {
      return (this.series || []).map((s) => {
        const pts = (s.points || [])
          .map((pt) => {
            const week = Number(pt.week);
            const progress = pointValue(pt);
            if (!Number.isFinite(week) || progress == null) return null;
            if (!(week in this.weekIndexMap)) return null;
            return {
              week,
              progress,
              x: this.progressX(progress),
              y: this.weekY(this.weekIndexMap[week]),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.week - b.week);
        return {
          key: s.key,
          label: s.label || s.key,
          color: s.color || '#1a90ff',
          stroke: s.stroke || s.color || '#0d6ecd',
          points: pts,
        };
      });
    },
    animatedSeries() {
      const p = easeOutCubic(this.animProgress);
      return this.normalizedSeries.map((s) => {
        if (!s.points.length) {
          return { key: s.key, color: s.color, path: '', opacity: 0 };
        }
        const visibleCount = Math.max(1, Math.ceil(s.points.length * Math.max(p, 0.001)));
        const pts = s.points.slice(0, visibleCount);
        let d = '';
        pts.forEach((pt, i) => {
          d += `${i === 0 ? 'M' : 'L'}${pt.x} ${pt.y} `;
        });
        return {
          key: s.key,
          color: s.color,
          path: d.trim(),
          opacity: Math.min(1, p * 1.2),
        };
      });
    },
    animatedDots() {
      const p = easeOutCubic(this.animProgress);
      // Compact single-series cards need larger radii; multi-series (All progress) stays smaller
      const multi = (this.series || []).length > 1;
      let baseR;
      let growR;
      let strokeWidth;
      if (this.expanded) {
        baseR = multi ? 2.8 : 3.6;
        growR = multi ? 0.9 : 1.2;
        strokeWidth = multi ? 1.2 : 1.4;
      } else if (multi) {
        baseR = 4.6;
        growR = 1.4;
        strokeWidth = 1.8;
      } else {
        baseR = 7.2;
        growR = 2.2;
        strokeWidth = 2.4;
      }
      const out = [];
      this.normalizedSeries.forEach((s) => {
        s.points.forEach((pt, idx) => {
          const start = (idx / Math.max(1, s.points.length)) * 0.35;
          const local = Math.min(1, Math.max(0, (p - start) / 0.65));
          const eased = easeOutCubic(local);
          out.push({
            key: s.key,
            x: pt.x,
            y: pt.y,
            r: baseR + growR * eased,
            color: s.color,
            stroke: '#ffffff',
            strokeWidth,
            opacity: eased,
          });
        });
      });
      return out;
    },
  },
  watch: {
    series: {
      deep: true,
      handler() {
        this.playIntro();
      },
    },
    weeks() {
      this.playIntro();
    },
  },
  mounted() {
    this.playIntro();
  },
  beforeDestroy() {
    this.cancelAnim();
  },
  methods: {
    progressX(value) {
      return this.plot.left + (value / 100) * this.plot.width;
    },
    weekY(index) {
      const n = Math.max(1, this.weeks.length - 1);
      return this.plot.bottom - (index / n) * this.plot.height;
    },
    cancelAnim() {
      if (this.animFrame) {
        cancelAnimationFrame(this.animFrame);
        this.animFrame = null;
      }
    },
    playIntro() {
      this.cancelAnim();
      this.animReady = false;
      this.animProgress = 0;
      const token = ++this.animToken;
      const duration = 1100;
      const start = performance.now();
      const tick = (now) => {
        if (token !== this.animToken) return;
        const raw = Math.min(1, (now - start) / duration);
        this.animProgress = raw;
        if (raw < 1) {
          this.animFrame = requestAnimationFrame(tick);
        } else {
          this.animProgress = 1;
          this.animReady = true;
          this.animFrame = null;
        }
      };
      this.animFrame = requestAnimationFrame(tick);
    },
  },
};
</script>

<style scoped>
.progress-line-chart {
  width: 100%;
  text-align: left;
}

.progress-line-chart.is-expanded {
  max-width: 760px;
  margin: 0 auto;
}

.progress-line-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: #4a4a4a;
}

.progress-line-body {
  display: flex;
  align-items: stretch;
  gap: 4px;
}

.progress-line-y-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 11px;
  font-weight: 700;
  color: #6d6e71;
  letter-spacing: 0.04em;
  align-self: center;
  padding: 0 2px;
}

.progress-line-plot-wrap {
  flex: 1 1 auto;
  min-width: 0;
}

.progress-line-svg {
  width: 100%;
  height: auto;
  display: block;
}

.zone-bg {
  fill: #fafbfc;
}

.progress-line-chart.is-tinted .zone-bg {
  fill: var(--chart-tint, #fafbfc);
}

.grid-h {
  stroke: #e4e7eb;
  stroke-width: 1;
}

.grid-v {
  stroke: #eef1f4;
  stroke-width: 1;
}

.axis-line {
  stroke: #9aa0a6;
  stroke-width: 1.2;
}

.axis-tick {
  stroke: #9aa0a6;
  stroke-width: 1;
}

.axis-label {
  fill: #6d6e71;
  font-size: 10px;
  font-weight: 600;
}

.axis-title {
  fill: #6d6e71;
  font-size: 11px;
  font-weight: 700;
}

.series-line {
  vector-effect: non-scaling-stroke;
}

.progress-line-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
  margin-top: 10px;
  justify-content: center;
}

.progress-line-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #4a4a4a;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1.5px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
}
</style>
